import React from "react";
import { Container, Reveal, HairlineList, Overline } from "@/components/primitives";
import FaqSection from "@/components/FaqSection";
import { complianceminderFaqs } from "@/content/faqs";
import { useDocumentMeta } from "@/lib/meta";
import {
  ProductHero,
  ContentSection,
  AdjacentCrossSell,
  ProductFinalCTA,
} from "@/components/ProductSections";

const IMG = "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c";

export default function ComplianceMinder() {
  useDocumentMeta({
    fullTitle: "SRA compliance software for law firms | ComplianceMinder",
    description:
      "SRA compliance software for law firms: versioned policies with director approval, a firm-wide risk assessment kept current, breach and incident registers, automatic staff training records and a compliance dashboard.",
    path: "/complianceminder",
  });
  return (
    <div data-testid="complianceminder-page">
      <ProductHero
        testid="complianceminder-hero"
        eyebrow="ComplianceMinder · Live compliance"
        title="Compliance without dependency."
        subhead="ComplianceMinder helps law firms move beyond spreadsheet drift, policy decay and expensive retainers by turning compliance into a live, inspectable system."
        primaryLabel="Book a ComplianceMinder demo"
        secondaryLabel="See how it works standalone"
        secondaryTo="#standalone"
        image={IMG}
      />

      {/* 1. Positioning */}
      <ContentSection
        n="01"
        eyebrow="Positioning"
        title="A compliance module in a box"
        testid="complianceminder-positioning"
      >
        <p className="text-lg leading-relaxed text-foreground">
          ComplianceMinder is a separate compliance product for law firms that want their complete
          policy set, firmwide risk, registers and remediation in one owner-controlled system.
        </p>
        <p className="mt-5 leading-relaxed text-muted-foreground">
          It is case-management-system agnostic, so it still makes sense whether a firm runs on a
          legacy practice-management platform, boxed legal software, or nothing structured at all.
          But it becomes even more useful when it can plug into live daily operational data.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {[
            "Legacy practice-management platforms",
            "Boxed legal software",
            "Spreadsheets & shared drives",
            "No case-management system",
          ].map((t) => (
            <span
              key={t}
              className="border border-border px-4 py-2 text-sm font-medium tracking-wide text-foreground/80"
            >
              {t}
            </span>
          ))}
        </div>
      </ContentSection>

      {/* 2. Horizon scanning — the operating loop */}
      <section className="border-b border-border py-24 md:py-32" data-testid="complianceminder-horizon">
        <Container>
          <Reveal>
            <div className="max-w-3xl">
              <Overline accent>Horizon scanning · The operating loop</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-6xl">
                Horizon scanning that turns into live policy updates.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                Twice a year, ComplianceMinder scans SRA, Lexcel and Law Society updates, turns those
                into proposed policy changes for director review, and once approved incorporates them
                into the firm's live working version while preserving the previous approved version
                with a full audit trail.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 border-l border-t border-border sm:grid-cols-2 lg:grid-cols-5">
            {[
              { n: "01", t: "Scan", d: "Twice-yearly scan of SRA, Lexcel and Law Society updates." },
              { n: "02", t: "Propose", d: "Relevant updates drafted as proposed policy changes." },
              { n: "03", t: "Directors approve", d: "Every change is put to the directors for sign-off." },
              { n: "04", t: "Publish live", d: "On approval, the live working version is updated." },
              {
                n: "05",
                t: "Preserve",
                d: "The previous approved version is archived and version-controlled in SharePoint.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="flex h-full flex-col border-b border-r border-border p-6"
                data-testid={`complianceminder-loop-step-${s.n}`}
              >
                <span className="font-display text-sm font-bold tracking-widest text-accent">{s.n}</span>
                <h3 className="mt-6 font-display text-xl font-bold tracking-[-0.02em] text-foreground">
                  {s.t}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>

          <Reveal>
            <p className="mt-10 max-w-2xl border-l-2 border-accent pl-5 text-base font-semibold leading-snug text-foreground">
              The result is a living policy set with a full audit trail — what changed, when, who
              approved it, and what the firm was working to before.
            </p>
          </Reveal>
        </Container>
      </section>

      {/* Symbiosis with CaseMinder */}
      <section className="border-b border-border py-24 md:py-32" data-testid="complianceminder-symbiosis">
        <Container>
          <Reveal>
            <div className="max-w-3xl">
              <Overline accent>Symbiotic with CaseMinder · Policy meets practice</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-6xl">
                When policy changes, operations should change with it.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                ComplianceMinder does not stop at policy review. It scans regulatory and standards
                updates, proposes changes for director approval, and updates the firm's live working
                policy set.
              </p>
              <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
                When connected to CaseMinder, those approved changes can then flow into the
                operational layer too — updating workflows, processes and prompts so the firm's
                day-to-day behaviour stays aligned with the policies it says it follows.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 border-l border-t border-border md:grid-cols-4">
            {[
              {
                n: "01",
                src: "ComplianceMinder",
                t: "Scan & propose",
                d: "Regulatory and standards updates become proposed policy changes.",
              },
              {
                n: "02",
                src: "ComplianceMinder",
                t: "Directors approve",
                d: "Changes are signed off and the live working policy set is updated.",
              },
              {
                n: "03",
                src: "CaseMinder",
                t: "Flow into workflow",
                d: "Approved changes feed into workflows, processes and prompts in the operating layer.",
              },
              {
                n: "04",
                src: "CaseMinder",
                t: "Behaviour changes",
                d: "The firm's day-to-day practice updates in step with the policy.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="flex h-full flex-col border-b border-r border-border p-6"
                data-testid={`complianceminder-symbiosis-step-${s.n}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-sm font-bold tracking-widest text-accent">{s.n}</span>
                  <span className="overline text-muted-foreground">{s.src}</span>
                </div>
                <h3 className="mt-6 font-display text-xl font-bold tracking-[-0.02em] text-foreground">
                  {s.t}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>

          <Reveal>
            <p className="mt-10 max-w-2xl border-l-2 border-accent pl-5 text-base font-semibold leading-snug text-foreground">
              A well-written policy is not enough on its own. If the firm is not actually working
              that way, the policy will not save it.
            </p>
          </Reveal>
        </Container>
      </section>

      {/* 3. Compliance dashboard — the visibility layer */}
      <section
        className="border-b border-border bg-secondary/40 py-24 md:py-32"
        data-testid="complianceminder-dashboard"
      >
        <Container>
          <Reveal>
            <div className="max-w-3xl">
              <Overline accent>The visibility layer · Compliance dashboard</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-6xl">
                The compliance dashboard owners should have had all along.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                ComplianceMinder gives directors, COLPs, COFAs, MLCOs and MLROs a live dashboard
                showing current compliance state, outstanding remediation, risk position, policy
                status, evidence trail and version history in one place.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
            {/* Dashboard mock */}
            <div className="lg:col-span-8">
              <div className="border border-border bg-background" data-testid="complianceminder-dashboard-mock">
                <div className="flex items-center justify-between border-b border-border px-6 py-4">
                  <span className="overline text-muted-foreground">Compliance dashboard</span>
                  <span className="overline text-muted-foreground">Illustrative</span>
                </div>
                <div className="grid grid-cols-2 border-b border-border sm:grid-cols-4 sm:divide-x sm:divide-border">
                  {[
                    { k: "Policies current", v: "42 / 44" },
                    { k: "Outstanding actions", v: "3" },
                    { k: "Firmwide risk", v: "Medium" },
                    { k: "Pending approvals", v: "2" },
                  ].map((t, i) => (
                    <div key={i} className="border-b border-border p-5 sm:border-b-0">
                      <div className="font-display text-3xl font-bold tracking-[-0.02em] text-foreground">
                        {t.v}
                      </div>
                      <span className="overline mt-2 block text-muted-foreground">{t.k}</span>
                    </div>
                  ))}
                </div>
                <div className="divide-y divide-border">
                  {[
                    ["AML policy updated", "Approved · v4 live · v3 archived", "2 days ago"],
                    ["File review remediation", "1 action outstanding", "Assigned"],
                    ["SRA horizon scan", "3 proposed changes awaiting directors", "This cycle"],
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-4 px-6 py-4">
                      <div>
                        <div className="text-sm font-semibold text-foreground">{r[0]}</div>
                        <div className="text-sm text-muted-foreground">{r[1]}</div>
                      </div>
                      <span className="overline shrink-0 text-muted-foreground">{r[2]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* What owners see */}
            <div className="lg:col-span-4">
              <Reveal delay={0.08}>
                <span className="overline text-muted-foreground">Visible at a glance</span>
                <HairlineList
                  testid="complianceminder-dashboard-list"
                  items={[
                    "Current compliance state across the firm",
                    "Outstanding remediation and actions",
                    "Live firmwide risk position",
                    "Policy status — current vs proposed",
                    "Evidence trail for every requirement",
                    "Version history and director approvals",
                  ]}
                />
              </Reveal>
            </div>
          </div>
        </Container>
      </section>

      {/* Firm-wide risk assessment */}
      <section className="border-b border-border py-24 md:py-32" data-testid="complianceminder-risk">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline accent>Firm-wide risk assessment</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-6xl">
                Always ready. Not rebuilt in a panic.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                The firm-wide risk assessment is mandated — and too often it gets thrown together the
                day before the SRA asks to see it. ComplianceMinder is the opposite of that.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Because it is fed by live compliance metrics, it holds an up-to-date picture of your
                complete compliance state at all times. The firm-wide risk assessment can be
                generated or refreshed at the touch of a button — with a downloadable PDF snapshot
                whenever you need one.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.08}>
              <div className="border border-border bg-background" data-testid="complianceminder-risk-mock">
                <div className="flex items-center justify-between border-b border-border px-6 py-4">
                  <span className="overline text-muted-foreground">Firm-wide risk assessment</span>
                  <span className="overline text-accent">Live</span>
                </div>
                <div className="flex items-center justify-between border-b border-border px-6 py-6">
                  <div>
                    <div className="font-display text-4xl font-bold tracking-[-0.02em] text-foreground">
                      Medium
                    </div>
                    <span className="overline mt-2 block text-muted-foreground">
                      Overall risk · refreshed 4 minutes ago
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled
                    className="inline-flex items-center gap-2 bg-primary px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-primary-foreground"
                  >
                    Download PDF
                  </button>
                </div>
                <div className="divide-y divide-border">
                  {[
                    ["AML & financial crime", "Medium"],
                    ["Client & matter risk", "Low"],
                    ["Data & information security", "Medium"],
                    ["Regulatory & SRA standards", "Low"],
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between px-6 py-4">
                      <span className="text-sm text-foreground/90">{r[0]}</span>
                      <span className="overline text-muted-foreground">{r[1]}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-border px-6 py-3">
                  <span className="overline text-muted-foreground">Illustrative · one-touch generate & refresh</span>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* 4. What it does */}
      <ContentSection
        n="02"
        eyebrow="Capability"
        title="Live policy. Live risk. Live evidence."
        testid="complianceminder-what"
      >
        <p className="leading-relaxed text-muted-foreground">
          ComplianceMinder houses the firm's complete policy suite as live controlled documents. It
          enables the firmwide risk assessment at the push of a button, then links that to
          registers, remediation and version-controlled updates.
        </p>
        <p className="mt-5 leading-relaxed text-muted-foreground">
          It scans SRA, Lexcel and Law Society updates twice a year, suggests policy changes for
          discussion with the law firm owners, and once approved updates the live version while
          archiving the old version to SharePoint with proper version control.
        </p>
        <HairlineList
          testid="complianceminder-what-list"
          items={[
            "Full policy library as live controlled documents",
            "Firmwide risk assessment engine",
            "Registers and remediation in one inspectable system",
            "Twice-yearly regulatory update scanning",
            "Suggested updates before owner approval",
            "SharePoint archiving and version control of prior versions",
          ]}
        />
      </ContentSection>

      {/* 3. Standalone vs integrated */}
      <section id="standalone" className="border-b border-border bg-secondary/40 py-24 md:py-28" data-testid="complianceminder-standalone">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline accent>Standalone vs integrated</Overline>
              <h2 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-foreground md:text-5xl">
                Useful on its own. Better with live data.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.08}>
              <p className="leading-relaxed text-muted-foreground">
                Standalone, ComplianceMinder gives firms structure where they currently have
                document sprawl: policies, registers, versioning, approvals, gap review and firmwide
                risk all in one place.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Integrated with CaseMinder or another live workflow source, it becomes more powerful
                because it can draw on actual daily operational signals: risk assessments, file
                reviews, source-of-funds patterns, completions data, audit logs and workflow
                behaviour. That means compliance can be measured against what the firm is really
                doing, not just what the handbook says.
              </p>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* 4. Traditional compliance problem */}
      <ContentSection
        n="03"
        eyebrow="The old way"
        title="The problem with traditional compliance support"
        testid="complianceminder-problem"
      >
        <p className="leading-relaxed text-muted-foreground">
          Too many firms are paying for compliance support that is expensive, inconsistent and
          invisible until inspection pressure arrives. Policies exist, but drift. Registers exist,
          but decay. Remediation exists, but lives in somebody's inbox.
        </p>
        <p className="mt-5 text-lg leading-relaxed text-foreground">
          ComplianceMinder replaces that with a system the owners can inspect directly.
        </p>
      </ContentSection>

      {/* 5. Why owners care */}
      <ContentSection
        n="04"
        eyebrow="Owner visibility"
        title="Owner visibility matters"
        testid="complianceminder-owners"
      >
        <p className="leading-relaxed text-muted-foreground">
          Law firm owners, COLPs, COFAs, MLCOs and MLROs should not need a retainer just to discover
          whether the firm's compliance exists in reality. They need to see current state, changes,
          outstanding remediation, evidence trail and version history for themselves.
        </p>
        <p className="mt-5 text-lg leading-relaxed text-foreground">That is what ComplianceMinder is for.</p>
        <div className="mt-8 grid grid-cols-2 divide-x divide-border border border-border sm:grid-cols-4">
          {["COLP", "COFA", "MLCO", "MLRO"].map((r, i) => (
            <div key={i} className="p-5">
              <span className="overline text-muted-foreground">{r}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Full compliance capability */}
      <section className="border-b border-border py-24 md:py-28" data-testid="complianceminder-capabilities">
        <Container>
          <Reveal>
            <div className="max-w-3xl">
              <Overline accent>The full capability set</Overline>
              <h2 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-foreground md:text-5xl">
                Compliance you can evidence, end to end.
              </h2>
              <p className="mt-6 leading-relaxed text-muted-foreground">
                A structured, evidenced way to run the firm's obligations — with an audit trail behind
                every action, and sensitive surfaces gated to the MLCO, MLRO, COLP and directors.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 border-l border-t border-border sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Versioned policy & approvals", d: "Edit a policy with a mandatory reason; every change creates a new version and a formal go-live step. Nothing is overwritten silently." },
              { t: "Attestations & control tests", d: "Staff attest to the current version of a named policy; scheduled control tests are recorded pass/fail with evidence and a clear overdue view." },
              { t: "Breach & incident logging", d: "Log a breach against a policy or matter with severity and reason — the right people are notified immediately, not after the fact." },
              { t: "MLCO → COLP review chain", d: "Red-flag and high-risk matters flow through a structured review queue to a recorded terminal COLP sign-off." },
              { t: "Firm-wide risk & evidence packs", d: "Matter risk rolls up into a firm-wide register and high-risk view, with downloadable evidence packs and monthly regulatory exports." },
              { t: "AML & client due diligence", d: "Per-matter AML completion signals, MLRO alerts, and director vouching for ID where automated checks can't reach." },
            ].map((c, i) => (
              <div key={i} className="flex h-full flex-col border-b border-r border-border p-6" data-testid={`complianceminder-capability-${i}`}>
                <h3 className="font-display text-lg font-bold tracking-[-0.02em] text-foreground">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <FaqSection
        eyebrow="ComplianceMinder FAQ"
        title="Questions owners ask"
        items={complianceminderFaqs}
        testid="complianceminder-faq"
        bg
      />

      {/* 6. Adjacent / cross-sell to CaseMinder */}
      <AdjacentCrossSell
        testid="complianceminder-adjacent"
        title="Feed live operational data into compliance"
        body="CaseMinder gives ComplianceMinder better fuel. If you want compliance driven by live operational data rather than static paperwork, connect the two — then add Electra HR for the people layer usually missing in smaller firms."
        to="/caseminder"
        label="Explore CaseMinder"
      />

      {/* 7. Final CTA */}
      <ProductFinalCTA
        testid="complianceminder-final-cta"
        title="Make compliance inspectable."
        body="Live policies, firmwide risk and evidence the owners can see for themselves."
        label="Book a ComplianceMinder demo"
      />
    </div>
  );
}
